<template>
  <div class="w-full flex flex-col items-start justify-start h-[400px]">
    <div class="p-0.5 w-fit space-x-4 rounded-full bg-[#F5F8FA] border border-[#B3CAD6] flex items-center">
      <div
        :class="{ 'bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] text-white': add_aggregation }"
        class="p-2 px-4 rounded-full cursor-pointer"
        @click="
          () => {
            add_aggregation = true
            current_metric.aggregation_method = 'sum'
          }
        ">
        {{ $t("Create as a Group KPI") }}
      </div>
      <div
        :class="{ 'bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] text-white': !add_aggregation }"
        class="p-2 rounded-full cursor-pointer px-4"
        @click="
          () => {
            add_aggregation = false
            current_metric.aggregation_method = null
          }
        ">
        {{ $t("Create as a KPI") }}
      </div>
    </div>

    <SelectInput
      v-if="add_aggregation"
      v-model:current-item="current_metric.aggregation_method"
      :value="current_metric.aggregation_method"
      :show-label="true"
      :items="metrics_aggregation_methods"
      name="agmeth"
      value-attr="value"
      label-attr="name"
      :placeholder="$t('Aggregation method')"
      label=""
      classes="w-1/2 mt-4"
      input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light " />

    <!-- Private or public metric -->
    <div class="flex mt-6 items-center justify-center text-base font-medium text-gray-700 dark:text-white">
      {{ $t("Public metric ?") }}
      <div class="float-right px-2">
        <Switch
          v-model="current_metric.is_public"
          :class="[
            current_metric.is_public == true ? 'bg-blue' : 'bg-gray-200',
            'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
          ]">
          <span
            aria-hidden="true"
            :class="[
              current_metric.is_public == true && $store.state.portfolio.ui_options?.language == 'ar'
                ? '-translate-x-5'
                : current_metric.is_public == true
                  ? 'translate-x-5'
                  : 'translate-x-0',
              'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
            ]" />
        </Switch>
      </div>
    </div>

    <div
      v-if="current_metric.metric_class == 'custom_metric'"
      class="flex items-center justify-center text-base font-medium text-gray-700 dark:text-white">
      {{ $t("Is Read Only ?") }}
      <div class="float-right px-2">
        <Switch
          v-model="current_metric.is_rs_readonly"
          :class="[
            current_metric.is_rs_readonly == true ? 'bg-blue' : 'bg-gray-200',
            'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
          ]">
          <span
            aria-hidden="true"
            :class="[
              current_metric.is_rs_readonly == true && $store.state.portfolio.ui_options?.language == 'ar'
                ? '-translate-x-5'
                : current_metric.is_rs_readonly == true
                  ? 'translate-x-5'
                  : 'translate-x-0',
              'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
            ]" />
        </Switch>
      </div>
    </div>

    <!-- Input type -->
    <div v-if="current_metric.data_type == 'Number'" class="flex flex-col justify-start w-full mt-3">
      <div class="block text-base font-medium text-gray-700 dark:text-white">
        {{ $t("Display as") }}
      </div>
      <div class="flex justify-start w-full gap-4">
        <SelectInput
          :items="number_metric_input_types"
          label-attr="label"
          value-attr="value"
          name="input_type"
          :return-ids="true"
          :allow-null="false"
          :value="current_metric.input_type"
          :placeholder="$t('Display as')"
          :label="$t('Display as')"
          classes="w-1/2"
          input-classes="form-input"
          @update:current-item="
            (val) => {
              current_metric.input_type = val
            }
          " />
        <TextInput
          v-if="current_metric.input_type == 'Decimal'"
          v-model="current_metric.input_options.decimal_places"
          type="text"
          name="decimal_places"
          :label="$t('Decimal Places')"
          classes="w-1/6"
          :validate-on-input="true"
          input-classes="form-input" />
      </div>
    </div>
    <div v-else-if="current_metric.data_type == 'String'" class="flex justify-start w-full">
      <SelectInput
        :value:current-item="current_metric.input_type"
        :items="string_metric_input_types"
        name="input_type"
        label-attr="label"
        value-attr="value"
        :allow-null="false"
        :return-ids="true"
        :placeholder="$t('Display as')"
        :label="$t('Display as')"
        classes="w-1/2"
        input-classes="form-input"
        @update:current-item="
          (val) => {
            current_metric.input_type = val
          }
        " />

      <SelectInput
        v-if="current_metric.input_type == 'Currency'"
        :value="current_metric.input_options.currency"
        :items="consts.curr"
        name="currency"
        label-attr="label"
        value-attr="value"
        :allow-null="false"
        :return-ids="true"
        :placeholder="$t('Currency')"
        :show-label="true"
        :label="$t('Currency')"
        classes="w-1/2"
        input-classes="form-input"
        @update:current-item="
          (val) => {
            current_metric.input_options['currency'] = val
          }
        " />
    </div>

    <div v-if="current_metric.input_type == 'Number' || current_metric.input_type == 'Currency'" class="flex justify-between">
      <div class="mt-2 text-common-grey">
        {{ $t("Truncate") }}:
      </div>
      <input
        v-model="current_metric.input_options.truncate"
        :checked="current_metric?.input_options?.truncate"
        type="checkbox"
        class="ml-2 mt-2 form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration">
    </div>
  </div>
</template>

<script>
import SelectInput from "@/components/shared/SelectInput.vue"
import TextInput from "@/components/shared/TextInput.vue"

import { consts } from "@/store/constants"
import { utils } from "@/store/helpers"
import { Switch } from "@headlessui/vue"

export default {
  components: {
    SelectInput,
    Switch,
    TextInput
  },
  props: {
    metric: {
      type: Object,
      required: true
    }
  },
  emits: ["metric_type", "update:modelValue", "update"],
  data() {
    return {
      metrics_aggregation_methods: [
        { name: this.$t("SUM"), value: "sum" },
        { name: this.$t("MIN"), value: "min" },
        { name: this.$t("MAX"), value: "max" },
        { name: this.$t("COUNT"), value: "count" },
        { name: this.$t("AVERAGE"), value: "average" },
        { name: this.$t("WEIGHTED"), value: "weighted" }
      ],
      add_aggregation: false,
      consts,
      current_metric: null,
      number_metric_input_types: [
        { label: this.$t("Number"), value: "Number" },
        { label: this.$t("Decimal"), value: "Decimal" },
        { label: this.$t("Percent"), value: "Percent" },
        { label: this.$t("Currency"), value: "Currency" },
        { label: this.$t("StarRating"), value: "StarRating" },
        { label: this.$t("BarRating"), value: "BarRating" }
      ],
      string_metric_input_types: [
        { label: this.$t("Text"), value: "Text" },
        { label: this.$t("Textarea"), value: "Textarea" },
        { label: this.$t("RichTextarea"), value: "RichTextarea" },
        { label: this.$t("URL"), value: "URL" }
      ]
    }
  },
  watch: {
    current_metric: {
      deep: true,
      handler() {
        if (this.current_metric) {
          this.$emit("update", this.current_metric)
        }
      }
    }
  },
  created() {
    this.current_metric = utils.clone(this.metric)
    if (utils.isEmpty(this.current_metric?.input_options)) {
      this.current_metric["input_options"] = { decimal_places: 2, truncate: true, currency: this.$t("USD") }
    }
  },
  mounted() {
    this.current_metric = utils.clone(this.metric)
    if (utils.isEmpty(this.current_metric?.input_options)) {
      this.current_metric["input_options"] = { decimal_places: 2, truncate: true, currency: this.$t("USD") }
    }
  }
}
</script>
